/**
 * useAnalytics Composable
 * Main interface for manual analytics tracking
 */

import { analyticsService } from '../services/analytics.service'
import type { AnalyticsIdentityScope } from '../services/analytics.service'
import type { AnalyticsEventName } from '../types'
import type { AnalyticsEventProperties } from '../types/properties'

export const useAnalytics = () => {
  /**
   * Track a custom event
   *
   * @example
   * analytics.track('post_created', { postId: '123', postType: 'discussion' })
   */
  const track = (event: AnalyticsEventName | string, properties?: AnalyticsEventProperties): void => {
    analyticsService.track(event, properties)
  }

  /**
   * Set global context for all events
   *
   * @example
   * analytics.setContext({ groupSlug: 'my-group', locationId: 'loc-123' })
   */
  const setContext = (context: Record<string, unknown>): void => {
    analyticsService.setContext(context)
  }

  /**
   * Update a single context property
   *
   * @example
   * analytics.updateContext('groupSlug', 'new-group')
   */
  const updateContext = (key: string, value: unknown): void => {
    analyticsService.updateContext(key, value)
  }

  /**
   * Get current context
   */
  const getContext = (): Record<string, unknown> => {
    return analyticsService.getContext()
  }

  /**
   * Clear all context
   */
  const clearContext = (): void => {
    analyticsService.clearContext()
  }

  /**
   * Record a platform-scoped identity for matching-location event envelopes.
   *
   * @example
   * analytics.identify(
   *   'contact-456',
   *   { appName: 'Client Portal' },
   *   { kind: 'platform', locationId: 'location-123' }
   * )
   */
  const identify = (contactId: string, traits: Record<string, unknown> | undefined, scope: AnalyticsIdentityScope): void => {
    analyticsService.identify(contactId, traits, scope)
  }

  return {
    track,
    identify,
    setContext,
    updateContext,
    getContext,
    clearContext,
  }
}
